import type {
  ActionPlatform,
  BreakPlatform,
  BreakPlaybookType,
  BreakSeverity,
} from '../types/breaks';

/** Brand text colors for platform glyphs. */
export const PLATFORM_TEXT: Record<ActionPlatform, string> = {
  slack: 'text-[#4A154B]',
  jira: 'text-[#0052CC]',
  confluence: 'text-[#1868DB]',
  'dynamics-crm': 'text-[#002050]',
  github: 'text-zinc-800',
  pagerduty: 'text-[#06AC38]',
  'bmc-helix': 'text-[#FE5000]',
  calendar: 'text-[#1A73E8]',
  intera: 'text-violet-600',
};

/** Soft circular icon-chip background per platform/action surface. */
export const PLATFORM_ICON_BG: Record<ActionPlatform, string> = {
  jira: 'bg-blue-50',
  confluence: 'bg-sky-50',
  slack: 'bg-fuchsia-50',
  'dynamics-crm': 'bg-indigo-50',
  github: 'bg-zinc-100',
  pagerduty: 'bg-emerald-50',
  'bmc-helix': 'bg-orange-50',
  calendar: 'bg-blue-50',
  intera: 'bg-violet-50',
};

/** Subtle card border + background tint per platform (onboarding ArtifactCard look). */
export const PLATFORM_CARD_TINT: Record<BreakPlatform, string> = {
  jira: 'border-blue-100 bg-blue-50/40',
  confluence: 'border-sky-100 bg-sky-50/40',
  slack: 'border-fuchsia-100 bg-fuchsia-50/30',
  'dynamics-crm': 'border-indigo-100 bg-indigo-50/40',
  github: 'border-zinc-200 bg-zinc-50/60',
  pagerduty: 'border-emerald-100 bg-emerald-50/40',
  'bmc-helix': 'border-orange-100 bg-orange-50/40',
};

/** Left-border color for the evidence blockquote, per platform. */
export const PLATFORM_QUOTE_BORDER: Record<BreakPlatform, string> = {
  jira: 'border-blue-300',
  confluence: 'border-sky-300',
  slack: 'border-fuchsia-300',
  'dynamics-crm': 'border-indigo-300',
  github: 'border-zinc-300',
  pagerduty: 'border-emerald-300',
  'bmc-helix': 'border-orange-300',
};

/** Footer divider color for the evidence card, per platform. */
export const PLATFORM_DIVIDER: Record<BreakPlatform, string> = {
  jira: 'border-blue-100',
  confluence: 'border-sky-100',
  slack: 'border-fuchsia-100',
  'dynamics-crm': 'border-indigo-100',
  github: 'border-zinc-200',
  pagerduty: 'border-emerald-100',
  'bmc-helix': 'border-orange-100',
};

/** Human-readable platform labels for "Open in {platform}" links. */
export const PLATFORM_LABEL: Record<BreakPlatform, string> = {
  slack: 'Slack',
  jira: 'Jira',
  confluence: 'Confluence',
  'dynamics-crm': 'Dynamics CRM',
  github: 'GitHub',
  pagerduty: 'PagerDuty',
  'bmc-helix': 'BMC Helix',
};

/** Labels for every surface an action can run on (includes Intera-native ops). */
export const ACTION_PLATFORM_LABEL: Record<ActionPlatform, string> = {
  slack: 'Slack',
  jira: 'Jira',
  confluence: 'Confluence',
  'dynamics-crm': 'Dynamics CRM',
  github: 'GitHub',
  pagerduty: 'PagerDuty',
  'bmc-helix': 'BMC Helix',
  calendar: 'Calendar',
  intera: 'Intera',
};

/** Playbook-type tag labels. */
export const PLAYBOOK_LABEL: Record<BreakPlaybookType, string> = {
  'recurring-mistake': 'Recurring mistake',
  'wrong-estimation': 'Wrong estimation',
  'team-not-looped-in': 'Team not looped in',
  'scope-creep': 'Scope creep',
  'stale-dependency': 'Stale dependency',
  'missing-rollback': 'Missing rollback',
  'resource-overload': 'Resource overload',
};

/** Severity dot styles, high→low. */
export const SEVERITY_DOT: Record<BreakSeverity, string> = {
  high: 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.55)]',
  medium: 'bg-amber-500',
  low: 'bg-zinc-300',
};

export const SEVERITY_LABEL: Record<BreakSeverity, string> = {
  high: 'High severity',
  medium: 'Medium severity',
  low: 'Low severity',
};

/** Sort order for surfacing the most urgent gaps first. */
export const SEVERITY_RANK: Record<BreakSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** Action-oriented urgency wording shown on the card. */
export const URGENCY_LABEL: Record<BreakSeverity, string> = {
  high: 'Critical',
  medium: 'Needs attention',
  low: 'Watch',
};

/** Urgency pill chrome (filled, ringed) by severity. */
export const SEVERITY_PILL: Record<BreakSeverity, string> = {
  high: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  medium: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  low: 'bg-zinc-100 text-zinc-600 ring-1 ring-inset ring-zinc-200',
};

/** Top accent bar on the card, colored by severity. */
export const SEVERITY_BAR: Record<BreakSeverity, string> = {
  high: 'bg-gradient-to-r from-rose-500 to-rose-400',
  medium: 'bg-gradient-to-r from-amber-500 to-amber-400',
  low: 'bg-zinc-200',
};

/** Resting ring + drop shadow that makes critical cards feel hot. */
export const SEVERITY_CARD_RING: Record<BreakSeverity, string> = {
  high: 'ring-1 ring-rose-200/70 shadow-[0_24px_60px_-24px_rgba(244,63,94,0.45)]',
  medium:
    'ring-1 ring-amber-100 shadow-[0_24px_60px_-28px_rgba(245,158,11,0.30)]',
  low: 'ring-1 ring-zinc-100 shadow-[0_20px_50px_-28px_rgba(0,0,0,0.18)]',
};

/** Stroke color for the hub→card connector line. */
export const SEVERITY_LINE: Record<BreakSeverity, string> = {
  high: 'rgba(244,63,94,0.55)',
  medium: 'rgba(245,158,11,0.45)',
  low: 'rgba(161,161,170,0.30)',
};

/** Stroke width for the hub→card connector line. */
export const SEVERITY_LINE_WIDTH: Record<BreakSeverity, number> = {
  high: 1.6,
  medium: 1.1,
  low: 0.8,
};
